import * as React from "react";
import { gql, useQuery, useMutation } from "@apollo/client";
import PageHeader from "../theme/components/common/PageHeader";
import Table from "react-bootstrap/Table";
import Card from "react-bootstrap/Card";
import Button from "react-bootstrap/Button";
import Modal from "react-bootstrap/Modal";
import Form from "react-bootstrap/Form";
import Row from "react-bootstrap/Row";
import Col from "react-bootstrap/Col";
import Pagination from "react-bootstrap/Pagination";
import { toast } from "react-toastify";
import debounce from "lodash/debounce";
import apiClient from "../api";
import { gettext } from "../i18n";
import SessionTimeout from "components/common/SessionTimeout";
import "./StationAliasPage.scss";

const ALIASES_PER_PAGE = 10;

const GET_ALIASES = gql`
  query GetAliases($searchQuery: String, $limit: Int!, $offset: Int!) {
    station_name_alias(
      where: {
        _or: [
          { raw_name: { _ilike: $searchQuery } }
          { canonical_name: { _ilike: $searchQuery } }
        ]
      }
      limit: $limit
      offset: $offset
      order_by: [{ provider: asc }, { raw_name: asc }]
    ) {
      id
      provider
      raw_name
      canonical_name
      note
    }
    station_name_alias_aggregate(
      where: {
        _or: [
          { raw_name: { _ilike: $searchQuery } }
          { canonical_name: { _ilike: $searchQuery } }
        ]
      }
    ) {
      aggregate {
        count
      }
    }
  }
`;

const GET_PROVIDERS = gql`
  query GetProviders {
    station_name_alias(distinct_on: [provider]) {
      provider
    }
  }
`;

interface StationAlias {
  id: number;
  provider: string;
  raw_name: string;
  canonical_name: string;
  note: string | null;
}

interface AliasFormData {
  provider: string;
  raw_name: string;
  canonical_name: string;
  note: string;
}

const StationAliasPage = () => {
  const [searchQuery, setSearchQuery] = React.useState("");
  const [debouncedSearchQuery, setDebouncedSearchQuery] = React.useState("");
  const [currentPage, setCurrentPage] = React.useState(1);
  const [showModal, setShowModal] = React.useState(false);
  const [editingAlias, setEditingAlias] = React.useState<StationAlias | null>(
    null,
  );
  const [providers, setProviders] = React.useState<string[]>([]);
  const [formData, setFormData] = React.useState<AliasFormData>({
    provider: "",
    raw_name: "",
    canonical_name: "",
    note: "",
  });
  const [showApplyConfirm, setShowApplyConfirm] = React.useState(false);
  const [affectedCount, setAffectedCount] = React.useState(0);

  const debouncedSetQuery = React.useMemo(
    () => debounce((value: string) => setDebouncedSearchQuery(value), 400),
    [],
  );

  React.useEffect(() => {
    debouncedSetQuery(searchQuery);
    setCurrentPage(1);
    return () => debouncedSetQuery.cancel();
  }, [searchQuery, debouncedSetQuery]);

  const { data, error, refetch } = useQuery(GET_ALIASES, {
    variables: {
      searchQuery: `%${debouncedSearchQuery}%`,
      limit: ALIASES_PER_PAGE,
      offset: (currentPage - 1) * ALIASES_PER_PAGE,
    },
  });

  const { data: providersData } = useQuery(GET_PROVIDERS);

  React.useEffect(() => {
    if (providersData?.station_name_alias) {
      const uniqueProviders = providersData.station_name_alias.map(
        (a: { provider: string }) => a.provider,
      );
      setProviders(uniqueProviders);
    }
  }, [providersData]);

  const totalCount = data?.station_name_alias_aggregate?.aggregate?.count || 0;
  const totalPages = Math.ceil(totalCount / ALIASES_PER_PAGE);
  const aliases = data?.station_name_alias || [];

  const handleOpenAdd = () => {
    setEditingAlias(null);
    setFormData({
      provider: providers.length > 0 ? providers[0] : "",
      raw_name: "",
      canonical_name: "",
      note: "",
    });
    setShowModal(true);
  };

  const handleOpenEdit = (alias: StationAlias) => {
    setEditingAlias(alias);
    setFormData({
      provider: alias.provider,
      raw_name: alias.raw_name,
      canonical_name: alias.canonical_name,
      note: alias.note || "",
    });
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setEditingAlias(null);
  };

  const handleSave = async () => {
    if (!formData.provider || !formData.raw_name || !formData.canonical_name) {
      toast.error(gettext("Please fill in the required fields"));
      return;
    }

    try {
      if (editingAlias) {
        await apiClient.request("/admin/edit-alias", {
          method: "POST",
          data: {
            id: editingAlias.id,
            ...formData,
          },
        });
        toast.success(gettext("Alias updated successfully"));
      } else {
        await apiClient.request("/admin/add-alias", {
          method: "POST",
          data: formData,
        });
        toast.success(gettext("Alias added successfully"));
      }
      handleCloseModal();
      refetch();
    } catch (e: any) {
      console.error(e);
      const errorMsg = editingAlias
        ? gettext("Error updating alias")
        : gettext("Error adding alias");
      toast.error(`${errorMsg}: ${e.message?.toString() || "Unknown error"}`);
    }
  };

  const handleDelete = async (alias: StationAlias) => {
    if (
      !window.confirm(gettext("Are you sure you want to delete this alias?"))
    ) {
      return;
    }

    try {
      await apiClient.request("/admin/delete-alias", {
        method: "POST",
        data: { id: alias.id },
      });
      toast.success(gettext("Alias deleted successfully"));
      refetch();
    } catch (e: any) {
      console.error(e);
      toast.error(
        `${gettext("Error deleting alias")}: ${e.message?.toString() || "Unknown error"}`,
      );
    }
  };

  const handleOpenApplyAll = async () => {
    try {
      const result = await apiClient.request("/admin/preview-apply-aliases", {
        method: "GET",
      });
      setAffectedCount(result.count || 0);
      setShowApplyConfirm(true);
    } catch (e: any) {
      console.error(e);
      toast.error(
        `${gettext("Error applying aliases")}: ${e.message?.toString() || "Unknown error"}`,
      );
    }
  };
  const handleApplyAll = async () => {
    if (affectedCount === 0) {
      toast.info(gettext("No stations to rename"));
      setShowApplyConfirm(false);
      return;
    }

    try {
      await apiClient.request("/admin/apply-aliases", {
        method: "POST",
      });
      toast.success(gettext("Aliases applied successfully"));
      setShowApplyConfirm(false);
    } catch (e: any) {
      console.error(e);
      toast.error(
        `${gettext("Error applying aliases")}: ${e.message?.toString() || "Unknown error"}`,
      );
    }
  };

  if (error) {
    console.error("StationAliasPage GQL error: ", error);
    return <SessionTimeout />;
  }

  return (
    <>
      <PageHeader
        title={gettext("Station Aliases")}
        description={gettext("Manage station name aliases for data importers")}
      >
        <div className="d-flex gap-2">
          <Button
            variant="outline-primary"
            size="sm"
            onClick={handleOpenApplyAll}
          >
            {gettext("Apply all")}
          </Button>
          <Button variant="primary" size="sm" onClick={handleOpenAdd}>
            {gettext("Add alias")}
          </Button>
        </div>
      </PageHeader>

      <Card className="mb-3">
        <Card.Body>
          <Row className="g-3 mb-3">
            <Col xs={12} lg={6}>
              <Form.Label className="fw-semibold">
                {gettext("Search")}
              </Form.Label>
              <Form.Control
                type="text"
                placeholder={gettext("Search by raw name or canonical name")}
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
            </Col>
          </Row>

          <div className="table-responsive">
            <Table className="alias-table" style={{ tableLayout: "auto" }}>
              <thead>
                <tr>
                  <th style={{ width: "15%" }}>{gettext("Provider")}</th>
                  <th style={{ width: "25%" }}>{gettext("Raw name")}</th>
                  <th style={{ width: "25%" }}>{gettext("Canonical name")}</th>
                  <th style={{ width: "20%" }}>{gettext("Note")}</th>
                  <th style={{ width: "15%" }}>{gettext("Actions")}</th>
                </tr>
              </thead>
              <tbody>
                {aliases.length === 0 ? (
                  <tr>
                    <td colSpan={5} className="text-center">
                      {gettext("No aliases found")}
                    </td>
                  </tr>
                ) : (
                  aliases.map((alias: StationAlias) => (
                    <tr key={alias.id}>
                      <td data-label={gettext("Provider")}>{alias.provider}</td>
                      <td data-label={gettext("Raw name")}>{alias.raw_name}</td>
                      <td data-label={gettext("Canonical name")}>
                        {alias.canonical_name}
                      </td>
                      <td data-label={gettext("Note")}>{alias.note || "-"}</td>
                      <td data-label={gettext("Actions")}>
                        <div className="d-flex flex-wrap gap-1">
                          <Button
                            variant="outline-primary"
                            size="sm"
                            onClick={() => handleOpenEdit(alias)}
                          >
                            {gettext("Edit")}
                          </Button>
                          <Button
                            variant="outline-danger"
                            size="sm"
                            onClick={() => handleDelete(alias)}
                          >
                            {gettext("Delete")}
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </Table>
          </div>

          {totalPages > 1 && (
            <Pagination className="alias-pagination justify-content-center mb-0">
              <Pagination.Prev
                onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                disabled={currentPage === 1}
              >
                {gettext("Previous")}
              </Pagination.Prev>
              <Pagination.Item disabled>
                {gettext("Page")} {currentPage} {gettext("of")} {totalPages}
              </Pagination.Item>
              <Pagination.Next
                onClick={() =>
                  setCurrentPage((p) => Math.min(totalPages, p + 1))
                }
                disabled={currentPage === totalPages}
              >
                {gettext("Next")}
              </Pagination.Next>
            </Pagination>
          )}
        </Card.Body>
      </Card>

      <Modal show={showModal} onHide={handleCloseModal} centered>
        <Modal.Header closeButton>
          <Modal.Title>
            {editingAlias ? gettext("Edit alias") : gettext("Add alias")}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form>
            <Form.Group className="mb-3">
              <Form.Label>{gettext("Provider")}</Form.Label>
              <Form.Select
                value={formData.provider}
                onChange={(e) =>
                  setFormData({ ...formData, provider: e.target.value })
                }
              >
                <option value="">{gettext("Select provider")}</option>
                {providers.map((provider) => (
                  <option key={provider} value={provider}>
                    {provider}
                  </option>
                ))}
              </Form.Select>
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>{gettext("Raw name")}</Form.Label>
              <Form.Control
                type="text"
                placeholder={gettext("Enter raw name")}
                value={formData.raw_name}
                onChange={(e) =>
                  setFormData({ ...formData, raw_name: e.target.value })
                }
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>{gettext("Canonical name")}</Form.Label>
              <Form.Control
                type="text"
                placeholder={gettext("Enter canonical name")}
                value={formData.canonical_name}
                onChange={(e) =>
                  setFormData({ ...formData, canonical_name: e.target.value })
                }
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>{gettext("Note")}</Form.Label>
              <Form.Control
                type="text"
                placeholder={gettext("Enter note (optional)")}
                value={formData.note}
                onChange={(e) =>
                  setFormData({ ...formData, note: e.target.value })
                }
              />
            </Form.Group>
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="outline-secondary" onClick={handleCloseModal}>
            {gettext("Cancel")}
          </Button>
          <Button variant="primary" onClick={handleSave}>
            {editingAlias ? gettext("Save changes") : gettext("Add")}
          </Button>
        </Modal.Footer>
      </Modal>

      <Modal
        show={showApplyConfirm}
        onHide={() => setShowApplyConfirm(false)}
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>{gettext("Apply all")}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p>
            {gettext(
              "This action will apply all aliases to existing stations in the database. Stations with Raw Name matching an alias will be renamed to its Canonical Name. This is a one-way operation and cannot be undone.",
            )}
          </p>
          <p className="fw-bold">
            {gettext(
              "Are you sure you want to apply all aliases? This will rename %1 stations.",
              affectedCount.toString(),
            )}
          </p>
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="outline-secondary"
            onClick={() => setShowApplyConfirm(false)}
          >
            {gettext("Cancel")}
          </Button>
          <Button variant="primary" onClick={handleApplyAll}>
            {gettext("Apply all")}
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default StationAliasPage;
